import type { NaoChegaram } from './naoPassaram';

/**
 * AS TRÊS PARCELAS DO NÓ — a régua ditada pelo dono (08/10):
 *
 * > *"o que foi concluído marca como sucesso, se deu algum log de erro ou
 * > warning soma com erro, fora isso, se a empresa não passar ou não tiver log
 * > ali então ela vai ser apresentada como 'não chegaram a processar'... daí
 * > assim a soma vai ficar empresas com sucesso + empresas com erro + empresa
 * > que não passou por aquele nó"*
 *
 *     sucesso + erro + naoChegou === universo do departamento
 *
 * ────────────────────────────────────────────────────────────────────────────
 * DE ONDE VEM CADA UM, e por que NÃO é uma fonte só
 *
 *   sucesso   `/resumo`     empresas cujo desfecho é concluído
 *   erro      `/resumo`     err + warn (o dono: "erro OU warning soma com erro")
 *   naoChegou `/cobertura`  quem não tem log neste nó
 *
 * A tentativa de usar a cobertura como fonte ÚNICA (08/10, revertida aqui)
 * zerou o ✕ na tela inteira: o nó exibia "100% de sucesso" ao lado de uma lista
 * com 147 falhas, que foi o print do dono. A causa é que `cobertura.empresas` e
 * `entraram.empresas` do GRUPO chegam praticamente iguais, então
 * `entraram − cobertura` dá ~0 e o erro desaparece.
 *
 * Erro é desfecho, e desfecho vive no `/resumo`. "Não ter log" é ausência, e só
 * a cobertura sabe disso. São perguntas diferentes — a soma fecha porque as
 * três partições são disjuntas por CONSTRUÇÃO, não porque vêm do mesmo lugar:
 * quem não tem log não aparece no `/resumo` deste nó, logo não pode estar em
 * `sucesso` nem em `erro`.
 */
export interface ParcelasDoNo {
  /** Concluíram neste nó — o ✓ verde. */
  sucesso: number;
  /** Erro OU alerta neste nó — o ✕ vermelho. */
  erro: number;
  /** Não passou / não tem log aqui — o `↳`. */
  naoChegou: number;
  /** O universo que as três parcelas somam. */
  base: number;
}

/**
 * Deriva as três parcelas.
 *
 * `null` quando a cobertura não respondeu: sem ela não dá para saber quem não
 * chegou, e as outras duas sozinhas não fecham nada.
 *
 * `sucesso` é o COMPLEMENTO (base − erro − naoChegou), não `done` do `/resumo`.
 * É o que garante o fechamento mesmo quando as duas rotas discordam sobre o
 * recorte — e elas discordam: medido em produção, a cobertura via 353 entradas
 * onde o resumo via 344. Tomar `done` cru deixaria uma sobra sem explicação na
 * tela, que é exatamente o que o dono conferia na calculadora.
 */
export const parcelasDoNo = (
  naoChegaram: NaoChegaram | null,
  /** `err + warn` do `/resumo` para este nó — ver `falhasDaMacro`. */
  falhas: number,
  /**
   * O UNIVERSO DO DEPARTAMENTO (`area.universo`), que é a base do `↳` desde
   * 09/10 — regra do dono: *"o L (não chegaram) é apenas pras que não rodaram
   * no agrupamento com base no total de empresas do universo (universo de
   * empresas daquele departamento de bots)"*.
   *
   * `undefined` cai na base publicada em `naoChegaram` (a união dos grupos),
   * que é o comportamento anterior.
   */
  universo?: number,
): ParcelasDoNo | null => {
  if (!naoChegaram) return null;
  const base = universo && universo > 0 ? universo : naoChegaram.base;
  // Quem RODOU no agrupamento. `chegaram` é a contagem de quem tem registro
  // aqui — e no payload de hoje ela coincide com "quem concluiu", porque o
  // `/cobertura` publica `entraram` e `cobertura` idênticos para o grupo
  // (medido em 09/10: 255|255, 199|199, 232|232 em todos os nós do Fiscal LP;
  // causa em `cobertura_service.py:712-714`, onde `do_fecho` alimenta os dois).
  //
  // Por isso o `↳` NÃO é derivado de `entraram`: ele é `universo − rodaram`,
  // que é a pergunta do dono e não depende daquela distinção quebrada.
  const rodaram = Math.min(naoChegaram.chegaram, base);
  const naoChegou = base - rodaram;
  // `erro` nunca estoura o que sobra: as rotas têm recortes diferentes e, sem o
  // teto, um `sucesso` NEGATIVO apareceria na tela como "✓ -3".
  const erro = Math.min(falhas, rodaram);
  // POR QUE O ✓ NÃO VEM DE `cobertura.empresas` (medido em 09/10).
  //
  // Depois que o mon#92 fez a rota distinguir `entraram` de `cobertura`, a
  // tentação óbvia foi ler o ✓ direto da cobertura (admin#825, revertido aqui).
  // A medição em produção mostrou que as duas fontes respondem a perguntas
  // diferentes, e que é o `/resumo` que responde a DESTE indicador.
  //
  // Auditor Notas Fiscais LP, restrito às 344 que `entraram`:
  //
  //   cobertura diz           255 concluíram   (fecho `ok`/`skip`)
  //   /resumo diz             147 com erro ou alerta, 197 sem
  //
  // As 58 de diferença são empresas que têm fecho `ok` E registro de erro ou
  // alerta. A régua do dono é explícita sobre elas: *"se deu algum log de erro
  // ou warning soma com erro"*. Logo o ✓ são as 197 — quem entrou e não
  // registrou falha —, não as 255.
  //
  // Um teto `Math.min(concluiram, rodaram - erro)` seria inerte hoje (medido:
  // não altera o resultado em NENHUM grupo) e errado quando mordesse, porque
  // tiraria do ✓ empresa sem erro nenhum. Fica fora de propósito.
  return { sucesso: rodaram - erro, erro, naoChegou, base };
};

/** A identidade que o dono confere na calculadora. */
export const parcelasFecham = (p: ParcelasDoNo): boolean =>
  p.sucesso + p.erro + p.naoChegou === p.base;
